import {
  Color,
  Line,
  LineBasicMaterial,
  Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  ShaderMaterial,
} from 'three';

/** How a layer is highlighted: hovered in the layer list, or inspected, see Glb.inspectedLayer. */
export type LayerHighlightKind = 'hovered' | 'inspected';

/**
 * How each kind of layer is highlighted: how opaque it is, so that what is in front of it shows
 * through, and how much it glows. Hovering is faint, as the mouse moves over many objects, while
 * an inspected layer stands out.
 */
const styles: Record<LayerHighlightKind, { color: Color; opacity: number; glow: number }> = {
  hovered: { color: new Color(0xffe082), opacity: 0.35, glow: 0.15 },
  inspected: { color: new Color(0x1e88e5), opacity: 0.7, glow: 0.4 },
};

/**
 * Drawn after the rest of the scene, so that the highlight is seen through what is in front of it.
 */
const highlightRenderOrder = 1000;

/**
 * Highlights the meshes and lines of layers of a glb, e.g. an object of an IFC model, by drawing
 * them in a color instead of their materials, which are given back when the highlight is cleared.
 * The highlight is seen through what is in front of it, e.g. the walls around a room, as an object
 * hovered in the layer list is often inside the model. The lines of drawings and the texts, which
 * have shaders of their own, aren't highlighted.
 */
export class LayerHighlight {
  /** The materials and draw orders of the highlighted objects, given back by clear. */
  private originals = new Map<
    Mesh | Line,
    { material: Material | Material[]; renderOrder: number }
  >();
  /** The highlight of each material, which keeps how it is drawn, such as its sides. */
  private materials = new Map<LayerHighlightKind, Map<Material, Material>>();

  /**
   * @param skip Objects that aren't highlighted, such as the wireframes of a selected asset.
   */
  constructor(private skip: (object: Object3D) => boolean) {}

  /**
   * Highlights the layers, the hovered one over the inspected one, and clears the highlight of the
   * layers that were highlighted before.
   */
  set(layers: Partial<Record<LayerHighlightKind, Object3D>>) {
    this.clear();

    for (const kind of ['inspected', 'hovered'] as const) {
      layers[kind]?.traverse((object) => {
        if ((object instanceof Mesh || object instanceof Line) && !this.skip(object)) {
          this.highlight(object, kind);
        }
      });
    }
  }

  /**
   * Gives the highlighted objects their materials back.
   */
  clear() {
    for (const [object, { material, renderOrder }] of this.originals) {
      object.material = material;
      object.renderOrder = renderOrder;
    }

    this.originals.clear();
  }

  /**
   * Clears the highlight and frees its materials, e.g. when the data of the glb is replaced, or when
   * its materials change, which the highlight is made from.
   */
  dispose() {
    this.clear();

    for (const materials of this.materials.values()) {
      materials.forEach((material) => material.dispose());
    }

    this.materials.clear();
  }

  private highlight(object: Mesh | Line, kind: LayerHighlightKind) {
    const original = this.originals.get(object) ?? {
      material: object.material,
      renderOrder: object.renderOrder,
    };
    const materials = Array.isArray(original.material) ? original.material : [original.material];

    if (materials.some((material) => material instanceof ShaderMaterial)) {
      return;
    }

    const highlighted = materials.map((material) => this.getMaterial(material, kind, object));
    this.originals.set(object, original);
    object.material = Array.isArray(original.material) ? highlighted : highlighted[0];
    object.renderOrder = highlightRenderOrder;
  }

  private getMaterial(material: Material, kind: LayerHighlightKind, object: Mesh | Line) {
    let materials = this.materials.get(kind);
    if (!materials) {
      materials = new Map();
      this.materials.set(kind, materials);
    }

    let highlight = materials.get(material);
    if (!highlight) {
      const { color, opacity, glow } = styles[kind];
      highlight =
        object instanceof Line
          ? new LineBasicMaterial({ color })
          : new MeshStandardMaterial({ color, emissive: color, emissiveIntensity: glow });

      highlight.side = material.side;
      highlight.transparent = true;
      highlight.opacity = opacity * material.opacity;
      highlight.depthTest = false;
      highlight.depthWrite = false;
      materials.set(material, highlight);
    }

    return highlight;
  }
}
